import { useTranslation } from 'react-i18next';
import { t as translate } from 'i18n';
import React, { useEffect, useState } from 'react';
import { Box, Button, Menu, MenuItem, Select, Tooltip, Typography, useMediaQuery } from '@mui/material';
import { keyframes } from '@emotion/react';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import KeyboardArrowDownRoundedIcon from '@mui/icons-material/KeyboardArrowDownRounded';
import { useTokens } from 'theme';

const EFFORTS = () => ({
  none: { label: translate('assistant.modelPickerNoReasoning'), detail: translate('assistant.modelPickerADirectAnswerWithoutAdditionalReasoning') },
  low: { label: translate('assistant.modelPickerFast'), detail: translate('assistant.modelPickerLightReasoningForSimpleQuestions') },
  medium: { label: translate('assistant.modelPickerBalanced'), detail: translate('assistant.modelPickerABalanceOfDepthAndResponseTime') },
  high: { label: translate('assistant.modelPickerDeep'), detail: translate('assistant.modelPickerMoreDetailedReasoningForComplexTasks') },
  xhigh: { label: translate('assistant.modelPickerVeryDeep'), detail: translate('assistant.modelPickerDetailedReasoningForMultiStepTasks') },
  max: { label: translate('assistant.modelPickerMaximum'), detail: translate('assistant.modelPickerTheGreatestAvailableReasoningDepth') },
});
const info = (effort) => EFFORTS()[effort] || { label: effort, detail: '' };

/** The catalog declares the model's effort order, from least to most. */
export function effortLevel(effort, efforts) {
  if (effort === 'none') return 0;
  const reasoning = efforts.filter((value) => value !== 'none');
  const index = reasoning.indexOf(effort);
  if (index < 0) return 0;
  if (reasoning.length === 1) return 5;
  return 1 + Math.round(index * 4 / (reasoning.length - 1));
}
const pulse = keyframes`
  0%, 100% { transform: scaleY(0.65); opacity: 0.6; }
  50% { transform: scaleY(1); opacity: 1; }
`;

/** Decorative scale; the selected level is also named in text. */
function EffortScale({ effort, efforts, busy = false }) {
  useTranslation();
  const t = useTokens();
  const level = effortLevel(effort, efforts);
  return (
    <Box aria-hidden="true" sx={{ display: 'inline-flex', alignItems: 'flex-end', gap: '3px', height: 20, flexShrink: 0 }}>
      {[1, 2, 3, 4, 5].map((bar) => (
        <Box key={bar} sx={{
          width: 3, height: 5 + bar * 3, borderRadius: 1, transformOrigin: 'bottom',
          bgcolor: bar <= level ? t.link : t.borderField,
          opacity: bar <= level ? 1 : 0.35,
          transform: bar <= level ? 'scaleY(1)' : 'scaleY(0.65)',
          transition: `transform 220ms ${bar * 25}ms ease, background-color 180ms ease, opacity 180ms ease`,
          ...(busy && bar <= level ? { animation: `${pulse} 1.2s ${bar * 100}ms ease-in-out infinite` } : {}),
          '@media (prefers-reduced-motion: reduce)': { transition: 'none', animation: 'none' },
        }} />
      ))}
    </Box>
  );
}

export default function ModelPicker({ models, families = [], family, choice, onChoice, disabled, busy }) {
  const { t: translate } = useTranslation();
  const t = useTokens();
  const [anchor, setAnchor] = useState(null);
  const reduceMotion = useMediaQuery('(prefers-reduced-motion: reduce)');
  useEffect(() => setAnchor(null), [disabled, choice?.model]);
  const model = models.find((m) => m.id === choice?.model);
  if (!model) return null;
  const familyOf = (m) => m.family || family?.id || 'default';
  const groups = Array.from(new Set(models.map(familyOf))).map((id) => ({
    id, label: families.find((f) => f.id === id)?.label || (family?.id === id ? family.label : id === 'default' ? translate('assistant.modelPickerModels') : id),
  }));
  const currentFamily = familyOf(model);
  const siblings = models.filter((m) => familyOf(m) === currentFamily);
  const pickModel = (next) => next && onChoice?.({ model: next.id, effort: next.default_effort || '' });
  const effort = model.efforts.includes(choice.effort) ? choice.effort : model.default_effort;
  const pickerSx = {
    fontSize: '0.8rem', color: t.ink, bgcolor: t.raised, borderRadius: 2,
    minHeight: 36, maxWidth: '100%',
    '& .MuiSelect-select': { py: 1, pl: 1.5, pr: '32px !important' },
    '&:hover': { bgcolor: `${t.raised}` },
    '&.Mui-focused': { outline: `2px solid ${t.focus}`, outlineOffset: 2 },
    '& .MuiSelect-icon': { color: t.inkMuted },
  };
  const popoverSx = {
    bgcolor: t.surface, backgroundImage: 'none', border: `1px solid ${t.borderField}`,
    borderRadius: 3, maxWidth: 'calc(100vw - 24px)',
    boxShadow: '0 12px 36px rgba(0, 0, 0, 0.18)',
  };
  const menuProps = {
    anchorOrigin: { vertical: -12, horizontal: 'left' },
    transformOrigin: { vertical: 'bottom', horizontal: 'left' },
    transitionDuration: reduceMotion ? 0 : 150,
    PaperProps: { sx: popoverSx, style: { minWidth: 220 } },
    MenuListProps: { sx: { p: 0.75 } },
  };
  const optionSx = {
    gap: 2, px: 1.5, py: 1.25, my: 0.5, minHeight: 40, borderRadius: 2, fontSize: '0.875rem',
    '&.Mui-selected': { bgcolor: `${t.highlight}` },
    '&.Mui-selected:hover': { bgcolor: `${t.raised}` },
  };
  return (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, alignItems: 'center', minWidth: 0 }}>
      <Select variant="standard" disableUnderline value={currentFamily}
        onChange={(e) => pickModel(models.find((m) => familyOf(m) === e.target.value))}
        renderValue={(value) => groups.find((group) => group.id === value)?.label}
        MenuProps={menuProps}
        disabled={disabled} inputProps={{ 'aria-label': translate('assistant.modelPickerModelFamily') }} sx={pickerSx}>
        {groups.map((group) => (
          <MenuItem key={group.id} value={group.id} sx={optionSx}>
            <Box component="span" sx={{ flexGrow: 1 }}>{group.label}</Box>
            <CheckRoundedIcon fontSize="small" sx={{ color: t.link, visibility: currentFamily === group.id ? 'visible' : 'hidden' }} />
          </MenuItem>
        ))}
      </Select>
      <Select variant="standard" disableUnderline value={model.id}
        onChange={(e) => pickModel(siblings.find((m) => m.id === e.target.value))}
        renderValue={() => model.label}
        MenuProps={menuProps}
        disabled={disabled} inputProps={{ 'aria-label': translate('assistant.modelPickerModel') }} sx={{ ...pickerSx, maxWidth: { xs: 200, sm: 280 } }}>
        {siblings.map((m) => (
          <MenuItem key={m.id} value={m.id} sx={optionSx}>
            <Box component="span" sx={{ flexGrow: 1 }}>{m.label}</Box>
            <CheckRoundedIcon fontSize="small" sx={{ color: t.link, visibility: model.id === m.id ? 'visible' : 'hidden' }} />
          </MenuItem>
        ))}
      </Select>
      {model.efforts.length > 0 && (
        <>
          <Tooltip title={translate('assistant.modelPickerReasoningEffort')}>
            <span>
              <Button size="small" color="inherit" disabled={disabled} onClick={(e) => setAnchor(e.currentTarget)}
                aria-label={translate('assistant.modelPickerEffort')} aria-haspopup="menu" aria-expanded={Boolean(anchor) && !disabled}
                endIcon={<KeyboardArrowDownRoundedIcon />} sx={{
                  color: t.ink, bgcolor: t.raised, borderRadius: 2, px: 1.5, gap: 1.25, minHeight: 36,
                  '&:hover': { bgcolor: `${t.raised}` },
                  '&.Mui-focusVisible': { outline: `2px solid ${t.focus}`, outlineOffset: 2 },
                }}>
                <EffortScale effort={effort} efforts={model.efforts} busy={busy} />
                {info(effort).label}
              </Button>
            </span>
          </Tooltip>
          <Menu anchorEl={anchor} open={Boolean(anchor) && !disabled} onClose={() => setAnchor(null)}
            transitionDuration={reduceMotion ? 0 : 150}
            anchorOrigin={menuProps.anchorOrigin} transformOrigin={menuProps.transformOrigin}
            MenuListProps={{ 'aria-label': translate('assistant.modelPickerReasoningDepth'), sx: { p: 0.75 } }}
            PaperProps={{ sx: { ...popoverSx, width: 350 } }}>
            <Typography variant="subtitle2" sx={{ px: 1.5, pt: 1, pb: 1.25 }}>{translate('assistant.modelPickerReasoningDepth')}</Typography>
            {model.efforts.map((value) => (
              <MenuItem key={value} selected={effort === value} role="menuitemradio" aria-checked={effort === value}
                onClick={() => { onChoice?.({ model: model.id, effort: value }); setAnchor(null); }}
                sx={{ ...optionSx, gap: 1.5, py: 1.5, whiteSpace: 'normal' }}>
                <EffortScale effort={value} efforts={model.efforts} />
                <Box sx={{ flexGrow: 1 }}>
                  <Typography variant="body2" fontWeight={600}>{info(value).label}</Typography>
                  {info(value).detail && <Typography variant="caption" sx={{ display: 'block', color: t.inkMuted, mt: 0.5 }}>{info(value).detail}</Typography>}
                </Box>
                {effort === value && <CheckRoundedIcon fontSize="small" sx={{ color: t.link }} />}
              </MenuItem>
            ))}
            <Typography variant="caption" sx={{ display: 'block', px: 1.5, pt: 1.25, pb: 1, color: t.inkMuted }}>{translate('assistant.modelPickerDeeperReasoningMayIncreaseResponseTimeAnd')}</Typography>
          </Menu>
        </>
      )}
    </Box>
  );
}
